import React from "react";
import styled from "@emotion/styled";
import { ErrorBoundary } from "@sentry/react";
import { Flex, Spacer, Text } from "styles/utility-components";

import Button from "components/ui/button/Button";
import EmptyState from "components/ui/empty_state/EmptyState";
import { generateEmailLink } from "lib/emailUtils";
import { useCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import { useSupportChat } from "lib/utils/useSupportChat";

type ErrorDetails = {
  error: unknown;
  componentStack: string;
  eventId: string;
};

type ErrorBoundaryFallbackProps = {
  resetError(): void;
  hideLivechat?: boolean;
} & ErrorDetails;

// Wrapped into a separate component so if this crashes, it won't take the rest of the fallback with it
const LivechatWrapper = () => {
  const { openSupportChat } = useSupportChat();
  return (
    <Button buttonType="tertiary" onClick={() => openSupportChat("Error Boundary")}>
      Chat with us
    </Button>
  );
};

// Pure (no hooks) so it can also serve as the fallback when the current-user lookup is unavailable.
const EmailLink = ({ details, userGuid }: { details: ErrorDetails; userGuid?: string }) => {
  const to = "cs@stotles.com";
  const subject = "Stotles App Crash Report";
  let emailLink: string;
  try {
    emailLink = generateEmailLink({
      to,
      subject,
      body: `Please describe what you were doing when this error occurred and anything else you think might be relevant to helping us resolve this issue.

The technical details are below:
  - URL: ${window.location.href}
  - User GUID: ${userGuid ?? "Unknown"}
  - Event ID: ${details.eventId}
  - Error: ${String(details.error)}
  - Component Stack: ${details.componentStack}
  `.trim(),
    });
  } catch (e) {
    console.error("Failed to generate email link:", e);
    emailLink = generateEmailLink({ to, subject });
  }
  return (
    <Button buttonType="tertiary" as="a" href={emailLink}>
      Email us
    </Button>
  );
};

const EmailWithCurrentUser = ({ details }: { details: ErrorDetails }) => {
  const { data: currentUser } = useCurrentUser();
  return <EmailLink details={details} userGuid={currentUser?.guid} />;
};

// Crash-report link that must never itself throw. useCurrentUser needs a QueryClientProvider, but
// the top-level error boundary in AppLayout/AppProviders renders above it — so isolate the lookup
// and degrade to a hook-free EmailLink (GUID "Unknown") rather than blanking the whole error screen.
const Email = ({ details }: { details: ErrorDetails }) => (
  <ErrorBoundary fallback={<EmailLink details={details} />}>
    <EmailWithCurrentUser details={details} />
  </ErrorBoundary>
);

export const ErrorBoundaryFallback: React.FC<ErrorBoundaryFallbackProps> = ({
  hideLivechat = false,
  ...errorDetails
}) => {
  const { error, componentStack, eventId } = errorDetails;
  return (
    <Wrapper column width="100%" justifyContent="safe center" alignItems="safe center" gap="16px">
      <EmptyState
        type="loadError"
        size="large"
        width="100%"
        heading="Oops! Something has gone wrong"
        description={
          <>
            We're sorry but an error has occured. Please try the following actions to resolve the
            issue.
            <Spacer vertical={8} />
            If the issue persists, contact support and provide the event ID: <code>{eventId}</code>
          </>
        }
        action={
          <Flex gap="8px">
            <Button buttonType="primary" onClick={() => window.location.reload()}>
              Refresh
            </Button>
            {/* Plain anchor (full reload), not a router link: this fallback can render above the
                app's RouterProvider, and a hard navigation is the safest recovery from a crash. */}
            {/* eslint-disable-next-line no-restricted-syntax -- intentional full reload: error fallback may render above RouterProvider, and a hard navigation cleanly recovers crashed app state */}
            <Button buttonType="secondary" as="a" href="/">
              Go to home
            </Button>
            {hideLivechat ? (
              <Email details={errorDetails} />
            ) : (
              <ErrorBoundary fallback={<Email details={errorDetails} />}>
                <LivechatWrapper />
              </ErrorBoundary>
            )}
          </Flex>
        }
      />
      <details>
        <summary>Error Details</summary>
        <Flex column gap="8px">
          <Text description>
            Event ID: <code>{eventId}</code>
          </Text>
          <Text description>
            Error: <code>{String(error)}</code>
          </Text>
          <Text description>
            Component Stack: <code>{componentStack}</code>
          </Text>
        </Flex>
      </details>
    </Wrapper>
  );
};

const Wrapper = styled(Flex)({
  maxWidth: "632px", // 600px + (16px * 2) padding
  margin: "0 auto",
  padding: "16px 16px 54px 16px", // Increased bottom to offset the Error Details so the rest is centered
  wordBreak: "break-word",
});
